import {
  Alert,
  Box,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { PropsWithChildren, SubmitEventHandler } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { QuestionProps } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.interface';

import { useTranslate } from '@/hooks/useTranslate';

import { QuestionCode } from '@/types/api/pep';

import { getAPIErrorMessage } from '@/utils/api';
import { isRichText, renderHTMLText } from '@/utils/string';

import {
  HintIconWrapper,
  ProtectedImg,
  QuestionRichText,
  TooltipRichText,
  ToolTipWrapper,
} from './QuestionDrawer.styled';

type MinorQuestionFormProps = {
  answerError?: Error | null;
  isLoading?: boolean;
  onSubmit?: SubmitEventHandler<HTMLFormElement>;
} & Omit<QuestionProps, 'onAnswered'> &
  PropsWithChildren;

export function MinorQuestionForm({
  index,
  total,
  question,
  image,
  answerError,
  isLoading,
  onSubmit,
  children,
}: MinorQuestionFormProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  return (
    <>
      <Typography variant="h3" textAlign="center" mt={4} mb={1}>
        {t('questionDrawer.questionForm.minorTitle')}
      </Typography>
      <Typography
        variant="body2"
        color={theme.palette.neutral[800]}
        textAlign="center"
      >
        {t('questionDrawer.questionForm.minorBody')}
      </Typography>
      {image || <ProtectedImg />}
      {answerError && (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(answerError)}
        </Alert>
      )}
      <Box alignSelf="stretch" component="form" onSubmit={onSubmit}>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          textAlign="center"
          mb={1}
        >
          {t('questionDrawer.questionForm.formBody', { no: index + 1, total })}
        </Typography>
        {question.code !== QuestionCode.MinorOther && (
          <Typography
            variant="h4"
            textAlign="center"
            mb={question.additionalInfo ? 2 : 4}
          >
            {question.question}{' '}
            {question.info && (
              <Tooltip
                title={
                  isRichText(question.info) ? (
                    <TooltipRichText>
                      {renderHTMLText(question.info)}
                    </TooltipRichText>
                  ) : (
                    question.info
                  )
                }
                placement="top"
                arrow
                enterTouchDelay={0}
              >
                <HintIconWrapper>
                  <Icon
                    name="info-outline"
                    color={theme => theme.palette.neutral[700]}
                  />
                </HintIconWrapper>
              </Tooltip>
            )}
          </Typography>
        )}
        {isRichText(question.additionalInfo) && (
          <QuestionRichText mb={4}>
            {renderHTMLText(question.additionalInfo)}
          </QuestionRichText>
        )}
        {question.additionalInfo && !isRichText(question.additionalInfo) && (
          <Typography
            variant="caption"
            color={theme.palette.text.secondary}
            textAlign="center"
            mb={4}
          >
            {question.additionalInfo}
          </Typography>
        )}
        {question.description && (
          <Typography variant="body2" textAlign="center" mt={1}>
            {question.description}{' '}
            {question.tooltip && (
              <Tooltip
                title={question.tooltip}
                placement="top"
                arrow
                enterTouchDelay={0}
              >
                <ToolTipWrapper>
                  <Icon
                    name="info-outline"
                    color={theme => theme.palette.neutral[700]}
                    sx={{ verticalAlign: 'middle' }}
                  />
                </ToolTipWrapper>
              </Tooltip>
            )}
          </Typography>
        )}
        <Stack direction="column" mt={4} gap={4} alignItems="stretch">
          {children}
        </Stack>
      </Box>
      {isLoading && <FullPageLoaderOverlay />}
    </>
  );
}
